import React, { useState, useEffect } from "react";
import getTranslatedString from "@helpers/GetTranslatedString";
import Collapse from "react-bootstrap/Collapse";
import Link from "next/link";
import { useRouter } from "next/router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faChevronUp,
    faExternalLinkAlt,
    faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import BorderedContainer from "@components/ui/bordered_container";
import { Image, Title } from "@components/ui";
import { FlexContainer } from "@components/ui/flex_container";
import classNames from "classnames";
import NavigatableListView from "./NavigatableListView/NavigatableListView";
import { useNavigatableContext } from "./NavigatableMenuContainer/NavigatableMenuContainer";

// Sameiginlegt fall fyrir að sýna tákn
const renderIcon = (icon, className) => {
    if (!icon) return null;

    // Athuga hvort icon sé component (React element)
    if (React.isValidElement(icon)) {
        return React.cloneElement(icon, { className });
    }

    // Ef ekki component, þá er það mynd
    return <Image src={icon} className={className} />;
};

export default function SideBar({ menu, hideOnMobile }) {
    const router = useRouter();
    const [showList, setShowList] = useState(false);
    const [activeNavigatableItem, setActiveNavigatableItem] = useState(null);
    const [shouldRenderList, setShouldRenderList] = useState(false);

    if (!menu) {
        return <></>;
    }

    // Check if current URL matches any navigatable item and auto-open list
    useEffect(() => {
        if (!menu) return;

        // Find if any menu item has a navigatable sub_page that matches current URL
        for (const menuItem of menu) {
            if (menuItem?.sub_pages) {
                for (const subPage of menuItem.sub_pages) {
                    if (subPage.navigatable && router.asPath.startsWith(subPage.link)) {
                        // Only auto-open if we're on a detail page (not the base list page)
                        const normalizedPath = router.asPath.split("?")[0].replace(/\/$/, "");
                        const normalizedBase = subPage.link.replace(/\/$/, "");
                        if (normalizedPath !== normalizedBase) {
                            setActiveNavigatableItem(subPage);
                            setShouldRenderList(true);
                            requestAnimationFrame(() => {
                                setShowList(true);
                            });
                            return;
                        }
                    }
                }
            }
        }
        // If we're on the base page, close the list
        setShowList(false);
        setTimeout(() => {
            setShouldRenderList(false);
            setActiveNavigatableItem(null);
        }, 300);
    }, [router.asPath, menu]);

    const handleShowNavigatableList = (subPage) => {
        setActiveNavigatableItem(subPage);
        setShouldRenderList(true);
        requestAnimationFrame(() => {
            setShowList(true);
        });
    };

    const handleBack = () => {
        setShowList(false);
        setTimeout(() => {
            setShouldRenderList(false);
            setActiveNavigatableItem(null);
        }, 300);
    };

    return (
        <>
            <nav
                className={classNames("desktop-menu-container", {
                    ["hide-on-mobile"]: hideOnMobile,
                })}
                style={{ position: "sticky", top: 80, marginBottom: 20 }}
            >
                <div className="sidebar-navigation-container">
                    <div
                        className={classNames("sidebar-content-wrapper", { "show-list": showList })}
                    >
                        <div className="sidebar-main-view">
                            {menu.map((menuItem) => {
                                return (
                                    <SideBarElement
                                        key={menuItem?.title + menuItem?.link}
                                        menuItem={menuItem}
                                        onShowNavigatableList={handleShowNavigatableList}
                                    />
                                );
                            })}
                        </div>
                        <div className="sidebar-list-view">
                            {shouldRenderList && activeNavigatableItem && (
                                <NavigatableListView
                                    useDataHook={activeNavigatableItem.useDataHook}
                                    titleValue={activeNavigatableItem.titleValue}
                                    menuItem={activeNavigatableItem}
                                    onBack={handleBack}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </nav>
        </>
    );
}

export function SideBarElement({ menuItem, onShowNavigatableList: onShowNavigatableListProp }) {
    if (!menuItem) {
        return <></>;
    }
    // Try to get handler from context first, then fall back to prop
    const contextHandler = useNavigatableContext();
    const onShowNavigatableList = onShowNavigatableListProp || contextHandler;

    console.log(
        "SideBarElement rendered for:",
        menuItem.title,
        "has onShowNavigatableList:",
        !!onShowNavigatableList,
    );
    const [isOpen, setIsOpen] = useState(menuItem.default_open);
    const router = useRouter();

    const hasLink = !!menuItem.link;
    const hasSubPages = menuItem?.sub_pages?.length > 0;

    const handleElementClick = (e) => {
        e.preventDefault();
        if (hasLink) {
            router.push(menuItem.link);
        } else if (hasSubPages) {
            e.stopPropagation();
            setIsOpen(!isOpen);
        }
    };

    const handleArrowClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    const isSelected = router.pathname === menuItem.link;

    return (
        <BorderedContainer>
            <a
                onClick={handleElementClick}
                className={classNames(
                    "d-flex align-items-center justify-content-between element-container",
                    { selected: isSelected },
                )}
                target={menuItem.isExternal ? "_blank" : ""}
            >
                <FlexContainer alignItems="center" style={{ color: "inherit" }}>
                    {renderIcon(menuItem.icon, "element-image")}
                    <Title size={6} className="element-title">
                        {getTranslatedString({
                            is: menuItem.title,
                            en: menuItem.title_en,
                            pl: menuItem.title_pl,
                        })}
                    </Title>
                </FlexContainer>
                {menuItem?.sub_pages?.length !== undefined &&
                    (hasLink && hasSubPages ? (
                        <button
                            onClick={handleArrowClick}
                            className="navigatable-arrow-button"
                            aria-label={isOpen ? "Loka undirvalmynd" : "Opna undirvalmynd"}
                            type="button"
                        >
                            <OpenClosedIndicator isOpen={isOpen} />
                        </button>
                    ) : (
                        <OpenClosedIndicator isOpen={isOpen} />
                    ))}
                {menuItem.isExternal && <StyledFontAwesomeIcon icon={faExternalLinkAlt} />}
            </a>
            <Collapse in={isOpen}>
                <div className="element-children-container">
                    {menuItem?.sub_pages?.map((subPage, index) => {
                        return (
                            <SideBarElementChild
                                menuItem={menuItem}
                                subPage={subPage}
                                index={index}
                                key={subPage?.title + subPage?.link}
                                onShowNavigatableList={onShowNavigatableList}
                            />
                        );
                    })}
                </div>
            </Collapse>
        </BorderedContainer>
    );
}

export function OpenClosedIndicator({ isOpen }) {
    return isOpen ? (
        <StyledFontAwesomeIcon icon={faChevronUp} />
    ) : (
        <StyledFontAwesomeIcon icon={faChevronDown} />
    );
}

export function StyledFontAwesomeIcon({ icon, style, className }) {
    return (
        <FontAwesomeIcon
            style={{
                height: 12,
                width: 12,
                color: "gray",
                alignSelf: "center",
                ...style,
            }}
            className={className}
            icon={icon}
        />
    );
}

function SideBarElementChild({ menuItem, subPage, index, onShowNavigatableList }) {
    if (menuItem === null || menuItem === undefined || subPage === null || subPage === undefined) {
        return <></>;
    }

    const router = useRouter();
    // For navigatable items, check if current URL starts with the link (includes detail pages)
    const isSelected = subPage.navigatable
        ? router.asPath.startsWith(subPage?.link)
        : router.asPath === subPage?.link;
    const [isDarkTheme, setIsDarkTheme] = useState(false);

    // Fylgjast með þemu breytingum
    useEffect(() => {
        // Uppfæra í byrjun
        const updateTheme = () => {
            const dataTheme = document.documentElement.getAttribute("data-theme");
            setIsDarkTheme(dataTheme === "dark");
        };

        updateTheme();

        // Búa til MutationObserver til að fylgjast með þemu breytingum
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === "attributes" && mutation.attributeName === "data-theme") {
                    updateTheme();
                }
            });
        });

        observer.observe(document.documentElement, { attributes: true });

        return () => observer.disconnect();
    }, []);

    const handleArrowClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log(
            "Arrow clicked for:",
            subPage.title,
            "onShowNavigatableList:",
            !!onShowNavigatableList,
        );
        if (onShowNavigatableList) {
            console.log("Calling onShowNavigatableList with:", subPage);
            onShowNavigatableList(subPage);
        } else {
            console.error("onShowNavigatableList is not defined!");
        }
    };

    const handleMainClick = () => {
        // For navigatable items, navigate to the base page when clicking the main area
        if (subPage.navigatable) {
            router.push(subPage.link);
        }
        // For non-navigatable items, let the Link handle navigation
    };

    const content = (
        <FlexContainer
            className={classNames("element-child-container", {
                selected: isSelected,
            })}
            justifyContent="between"
            style={{
                padding: "7px 15px",
                borderBottomWidth: 1,
                borderBottomStyle: index !== menuItem?.sub_pages.length - 1 && "dotted",
                borderBottomColor: isDarkTheme ? "#555" : undefined,
                cursor: "pointer",
            }}
        >
            <div style={{ color: "inherit", display: "flex", alignItems: "center" }}>
                {renderIcon(subPage.icon, "element-child-image")}
                {getTranslatedString({
                    is: subPage.title,
                    en: subPage.title_en,
                    pl: subPage.title_pl,
                })}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {subPage.isExternal && <StyledFontAwesomeIcon icon={faExternalLinkAlt} />}
                {subPage.navigatable && (
                    <button
                        onClick={handleArrowClick}
                        className="navigatable-arrow-button"
                        aria-label="Opna lista"
                    >
                        <StyledFontAwesomeIcon icon={faChevronRight} />
                    </button>
                )}
            </div>
        </FlexContainer>
    );

    if (subPage.navigatable) {
        return (
            <div key={index} onClick={handleMainClick}>
                {content}
            </div>
        );
    }

    return (
        <Link href={subPage?.link || ""} key={index}>
            <a target={subPage.isExternal ? "_blank" : ""}>{content}</a>
        </Link>
    );
}
